import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { emailZaKorisnickoIme, jeUpravaUloga } from '../lib/konstante';
import { provjeriKoordinate } from '../lib/evidencija';
import { pripremiZaSpremanje } from '../lib/unosKucanstva';
import { SPREMNIK_DOKUMENATA, putanjaDokumenta } from '../lib/dokumenti';

function porukaGreske(greska) {
  if (greska?.code === '23505')
    return 'Adresa već postoji u evidenciji. Otvori postojeće kućanstvo.';
  if (['P0001', '40001'].includes(greska?.code)) return greska.message;
  if (['23514', '22P02', '23503', '22003'].includes(greska?.code))
    return 'Provjeri obavezna polja, brojeve, naselje i e-mail adrese.';
  if (/fetch|network|timeout|load failed/i.test(greska?.message ?? ''))
    return 'Veza s bazom nije dostupna. Provjeri internetsku vezu i pokušaj ponovno.';
  if (greska?.code === '42501')
    return 'Nemaš ovlasti za ovu radnju. Obrati se upravi.';
  if (['PGRST205', '42P01'].includes(greska?.code))
    return 'Evidencija još nije postavljena u bazi. Uprava treba provjeriti migracije.';
  return 'Baza nije uspjela obraditi zahtjev. Pokušaj ponovno ili se obrati upravi.';
}

export async function prijaviSe(korisnickoIme, lozinka) {
  const email = emailZaKorisnickoIme(korisnickoIme);
  let rezultat;
  try {
    rezultat = await supabase.auth.signInWithPassword({
      email,
      password: lozinka,
    });
  } catch (greska) {
    throw new Error(porukaGreske(greska));
  }
  if (rezultat.error) {
    const tekst = rezultat.error.message;
    if (/invalid login credentials|invalid grant/i.test(tekst))
      throw new Error('Korisničko ime ili lozinka nisu točni.');
    if (/email not confirmed/i.test(tekst))
      throw new Error('Račun još nije potvrđen. Obrati se upravi.');
    if (rezultat.error.status === 429)
      throw new Error('Previše pokušaja prijave. Pričekaj nekoliko minuta.');
    throw new Error(porukaGreske(rezultat.error));
  }
}

function ocistiNacrte() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('vodovod-nacrt:'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* Preglednik može onemogućiti lokalnu pohranu. */
  }
}
export async function odjaviSe() {
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  if (error) throw new Error('Odjava nije uspjela. Pokušaj ponovno.');
  ocistiNacrte();
}

/** Profil u bazi jedini određuje ovlasti; metapodaci prijave nisu izvor uloge. */
export function useSesija() {
  const [sesija, postaviSesiju] = useState(null);
  const [profil, postaviProfil] = useState(null);
  const [ucitava, postaviUcitava] = useState(true);
  const [greska, postaviGresku] = useState(null);
  const [pokusaj, postaviPokusaj] = useState(0);

  useEffect(() => {
    let aktivno = true;
    let primljenaPromjena = false;
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!aktivno || primljenaPromjena) return;
        if (error) postaviGresku(porukaGreske(error));
        postaviSesiju(data.session);
        if (!data.session) postaviUcitava(false);
      })
      .catch(() => {
        if (aktivno && !primljenaPromjena) postaviUcitava(false);
      });
    const { data } = supabase.auth.onAuthStateChange(
      (_dogadjaj, novaSesija) => {
        if (!aktivno) return;
        primljenaPromjena = true;
        postaviSesiju(novaSesija);
        if (!novaSesija) {
          ocistiNacrte();
          postaviProfil(null);
          postaviGresku(null);
          postaviUcitava(false);
        }
      },
    );
    return () => {
      aktivno = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const korisnikId = sesija?.user.id;
  useEffect(() => {
    if (!korisnikId) return;
    const prekid = new AbortController();
    postaviUcitava(true);
    postaviProfil(null);
    postaviGresku(null);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('profili')
          .select('id, ime, prezime, uloga, korisnicko_ime, clan_id')
          .eq('id', korisnikId)
          .abortSignal(prekid.signal)
          .maybeSingle();
        if (prekid.signal.aborted) return;
        if (error) postaviGresku(porukaGreske(error));
        else postaviProfil(data);
      } catch (e) {
        if (!prekid.signal.aborted) postaviGresku(porukaGreske(e));
      } finally {
        if (!prekid.signal.aborted) postaviUcitava(false);
      }
    })();
    return () => prekid.abort();
  }, [korisnikId, pokusaj]);

  const osvjeziProfil = useCallback(() => postaviPokusaj((p) => p + 1), []);
  return {
    sesija,
    profil,
    jeUprava: jeUpravaUloga(profil?.uloga),
    ucitava,
    greska,
    osvjeziProfil,
  };
}

/** Promjena korisnika prekida stari upit i odmah skriva prethodno učitane podatke. */
function useEvidencija(korisnikId, pogled, stupac) {
  const [stanje, postaviStanje] = useState({
    korisnikId: null,
    zapisi: [],
    ucitava: true,
    greska: null,
  });
  const prekid = useRef(null);
  const trenutniKorisnik = useRef(korisnikId);
  trenutniKorisnik.current = korisnikId;

  const osvjezi = useCallback(async () => {
    if (!korisnikId) return;
    prekid.current?.abort();
    const zahtjev = new AbortController();
    prekid.current = zahtjev;
    postaviStanje((s) => ({ ...s, korisnikId, ucitava: true, greska: null }));
    try {
      const { data, error } = await supabase
        .from(pogled)
        .select('*')
        .order(stupac)
        .abortSignal(zahtjev.signal);
      if (zahtjev.signal.aborted || trenutniKorisnik.current !== korisnikId)
        return;
      postaviStanje({
        korisnikId,
        zapisi: error ? [] : (data ?? []),
        ucitava: false,
        greska: error ? porukaGreske(error) : null,
      });
      return !error;
    } catch (e) {
      if (!zahtjev.signal.aborted && trenutniKorisnik.current === korisnikId)
        postaviStanje({
          korisnikId,
          zapisi: [],
          ucitava: false,
          greska: porukaGreske(e),
        });
      return false;
    }
  }, [korisnikId, pogled, stupac]);

  useEffect(() => {
    osvjezi();
    return () => prekid.current?.abort();
  }, [osvjezi]);
  const pripadaKorisniku = korisnikId && stanje.korisnikId === korisnikId;
  return {
    zapisi: pripadaKorisniku ? stanje.zapisi : [],
    ucitava: pripadaKorisniku ? stanje.ucitava : !!korisnikId,
    greska: pripadaKorisniku ? stanje.greska : null,
    osvjezi,
  };
}

async function provjeriUpis(zahtjev) {
  const { data, error } = await zahtjev.select('id');
  if (error) throw new Error(porukaGreske(error));
  if (!data?.length)
    throw new Error(
      'Promjena nije spremljena. Zapis više ne postoji ili nemaš ovlasti za izmjenu.',
    );
}

async function osvjeziNakonUpisa(osvjezi) {
  if (!(await osvjezi()))
    throw new Error(
      'Promjena je spremljena, ali osvježavanje prikaza nije uspjelo. Ponovno učitaj evidenciju.',
    );
}

export function useClanovi(korisnikId) {
  const { zapisi: clanovi, ...stanje } = useEvidencija(
    korisnikId,
    'v_karta',
    'redni_broj',
  );
  const { osvjezi } = stanje;
  const spremiTocku = async (clan, sirina, duzina) => {
    const [lat, lng] = provjeriKoordinate(sirina, duzina);
    await provjeriUpis(
      supabase.from('prikljucci').upsert(
        {
          clan_id: clan.id,
          geom: `SRID=4326;POINT(${lng} ${lat})`,
          status: 'potvrdjeno',
          podrijetlo: 'rucno',
          potvrdio: korisnikId,
          potvrdjeno_at: new Date().toISOString(),
        },
        { onConflict: 'clan_id' },
      ),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  const potvrdiTocku = async (clan) => {
    if (
      !['predlozeno', 'smjesteno'].includes(clan.status) ||
      clan.lat == null ||
      clan.lng == null
    )
      throw new Error('Ova točka nije dostupna za potvrdu.');
    await provjeriUpis(
      supabase
        .from('prikljucci')
        .update({
          status: 'potvrdjeno',
          potvrdio: korisnikId,
          potvrdjeno_at: new Date().toISOString(),
        })
        .eq('clan_id', clan.id)
        .in('status', ['predlozeno', 'smjesteno']),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  const ukloniTocku = async (clan) => {
    // Zadržava vodomjer i vezu na dionicu; uklanja se samo lokacija.
    await provjeriUpis(
      supabase
        .from('prikljucci')
        .update({
          geom: null,
          status: 'ne',
          podrijetlo: null,
          potvrdio: null,
          potvrdjeno_at: null,
        })
        .eq('clan_id', clan.id),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  return { clanovi, ...stanje, spremiTocku, potvrdiTocku, ukloniTocku };
}

export function useKucanstva(korisnikId) {
  const { zapisi: kucanstva, ...stanje } = useEvidencija(
    korisnikId,
    'v_kucanstva',
    'naselje',
  );
  const { osvjezi } = stanje;
  const spremiTocku = async (kucanstvo, sirina, duzina) => {
    const [lat, lng] = provjeriKoordinate(sirina, duzina);
    await provjeriUpis(
      supabase
        .from('kucanstva')
        .update({
          geom: `SRID=4326;POINT(${lng} ${lat})`,
          status_tocke: 'potvrdjeno',
        })
        .eq('id', kucanstvo.id),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  const potvrdiTocku = async (kucanstvo) => {
    if (
      !['predlozeno', 'smjesteno'].includes(kucanstvo.status) ||
      kucanstvo.lat == null ||
      kucanstvo.lng == null
    )
      throw new Error('Ova točka nije dostupna za potvrdu.');
    await provjeriUpis(
      supabase
        .from('kucanstva')
        .update({ status_tocke: 'potvrdjeno' })
        .eq('id', kucanstvo.id)
        .in('status_tocke', ['predlozeno', 'smjesteno']),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  const ukloniTocku = async (kucanstvo) => {
    await provjeriUpis(
      supabase
        .from('kucanstva')
        .update({ geom: null, status_tocke: 'ne', tocnost_m: null })
        .eq('id', kucanstvo.id),
    );
    await osvjeziNakonUpisa(osvjezi);
  };
  const spremiKucanstvo = async ({
    podaci,
    kontakti,
    zahtjev,
    id = null,
    ocekivano = null,
  }) => {
    const pripremljeno = pripremiZaSpremanje(podaci, kontakti);
    const { data, error } = await supabase.rpc('spremi_kucanstvo', {
      p_podaci: pripremljeno.podaci,
      p_kontakti: pripremljeno.kontakti,
      p_zahtjev: zahtjev,
      p_id: id,
      p_ocekivano: ocekivano,
      p_izvor: 'teren',
    });
    if (error) {
      if (error.code === '23505') await osvjezi();
      throw new Error(porukaGreske(error));
    }
    return { ...data, osvjezeno: await osvjezi() };
  };
  const uveziKucanstva = async (zapisi) => {
    const pripremljeni = zapisi.map((z) => ({
      ...pripremiZaSpremanje(z.podaci, z.kontakti),
      zahtjev: z.zahtjev,
    }));
    const { data, error } = await supabase.rpc('uvezi_kucanstva', {
      p_zapisi: pripremljeni,
    });
    if (error) {
      if (error.code === '23505') await osvjezi();
      throw new Error(porukaGreske(error));
    }
    return { ...data, osvjezeno: await osvjezi() };
  };
  return {
    kucanstva,
    ...stanje,
    spremiTocku,
    potvrdiTocku,
    ukloniTocku,
    spremiKucanstvo,
    uveziKucanstva,
  };
}

/** RLS odlučuje koji su kontakti vidljivi prijavljenom korisniku. */
export function useKontakti(kucanstvoId) {
  const [stanje, postaviStanje] = useState({
    id: null,
    kontakti: [],
    ucitava: false,
    greska: null,
  });
  useEffect(() => {
    if (!kucanstvoId) return;
    const prekid = new AbortController();
    postaviStanje({
      id: kucanstvoId,
      kontakti: [],
      ucitava: true,
      greska: null,
    });
    (async () => {
      try {
        const { data, error } = await supabase
          .from('kontakti')
          .select(
            'id, ime, prezime, veza, telefon, email, telefon_nositelja, glavni',
          )
          .eq('kucanstvo_id', kucanstvoId)
          .order('glavni', { ascending: false })
          .abortSignal(prekid.signal);
        if (!prekid.signal.aborted)
          postaviStanje({
            id: kucanstvoId,
            kontakti: error ? [] : (data ?? []),
            ucitava: false,
            greska: error ? porukaGreske(error) : null,
          });
      } catch (e) {
        if (!prekid.signal.aborted)
          postaviStanje({
            id: kucanstvoId,
            kontakti: [],
            ucitava: false,
            greska: porukaGreske(e),
          });
      }
    })();
    return () => prekid.abort();
  }, [kucanstvoId]);
  return stanje.id === kucanstvoId
    ? stanje
    : { kontakti: [], ucitava: !!kucanstvoId, greska: null };
}

/** Uređivanje koristi svježu verziju i sve kontakte koje RLS dopušta korisniku. */
export async function dohvatiKucanstvoZaUredjivanje(id) {
  const [zapis, kontakti] = await Promise.all([
    supabase.from('v_kucanstva').select('*').eq('id', id).single(),
    supabase
      .from('kontakti')
      .select(
        'id, ime, prezime, veza, telefon, email, telefon_nositelja, glavni',
      )
      .eq('kucanstvo_id', id)
      .order('glavni', { ascending: false }),
  ]);
  if (zapis.error || kontakti.error)
    throw new Error(porukaGreske(zapis.error || kontakti.error));
  return { zapis: zapis.data, kontakti: kontakti.data ?? [] };
}

export async function dohvatiKorisnickeUloge() {
  const { data, error } = await supabase
    .from('profili')
    .select('id, korisnicko_ime, ime, uloga')
    .order('korisnicko_ime');
  if (error) throw new Error(porukaGreske(error));
  return data ?? [];
}
export async function spremiKorisnickuUlogu(id, uloga) {
  await provjeriUpis(supabase.from('profili').update({ uloga }).eq('id', id));
}

/** Dokumenti su privatni; i metapodatke i datoteke ograničava RLS. */
export function useDokumenti(korisnikId, kucanstvoId = null) {
  const [stanje, postaviStanje] = useState({
    kljuc: '',
    dokumenti: [],
    ucitava: true,
    greska: null,
  });
  const kljuc = `${korisnikId ?? ''}:${kucanstvoId ?? ''}`;
  const zahtjev = useRef(0);
  const osvjezi = useCallback(async () => {
    const redni = ++zahtjev.current;
    if (!korisnikId) {
      postaviStanje({ kljuc, dokumenti: [], ucitava: false, greska: null });
      return;
    }
    postaviStanje((s) => ({
      ...s,
      dokumenti: s.kljuc === kljuc ? s.dokumenti : [],
      kljuc,
      ucitava: true,
      greska: null,
    }));
    try {
      let upit = supabase
        .from('dokumenti')
        .select('*')
        .order('created_at', { ascending: false });
      if (kucanstvoId) upit = upit.eq('kucanstvo_id', kucanstvoId);
      const { data, error } = await upit;
      if (error) throw error;
      if (redni === zahtjev.current)
        postaviStanje({
          kljuc,
          dokumenti: data ?? [],
          ucitava: false,
          greska: null,
        });
    } catch (e) {
      if (redni === zahtjev.current)
        postaviStanje({
          kljuc,
          dokumenti: [],
          ucitava: false,
          greska: porukaGreske(e),
        });
    }
  }, [korisnikId, kucanstvoId, kljuc]);
  useEffect(() => {
    osvjezi();
    return () => {
      zahtjev.current++;
    };
  }, [osvjezi]);
  return {
    ...(stanje.kljuc === kljuc
      ? stanje
      : { dokumenti: [], ucitava: true, greska: null }),
    osvjezi,
  };
}

export async function posaljiDokumente({
  datoteke,
  kucanstvoId = null,
  vrsta,
  naziv,
  datumPrivole = null,
  naNapredak = () => {},
}) {
  if (!datoteke.length || datoteke.length > 10)
    throw new Error('Odaberi od jedne do deset datoteka.');
  const {
    data: { user },
    error: greskaKorisnika,
  } = await supabase.auth.getUser();
  if (greskaKorisnika || !user)
    throw new Error('Ponovno se prijavi prije slanja dokumenta.');
  let poslano = 0,
    ponovljeno = 0;
  for (let i = 0; i < datoteke.length; i++) {
    const d = datoteke[i];
    naNapredak(`Šaljem dokument ${i + 1} od ${datoteke.length}…`);
    const podaci = {
      id: d.id,
      kucanstvo_id: kucanstvoId,
      vrsta,
      naziv: (naziv?.trim() || d.izvorni_naziv).slice(0, 200),
      izvorni_naziv: d.izvorni_naziv,
      mime: d.mime,
      velicina: d.velicina,
      sha256: d.sha256,
      putanja: putanjaDokumenta(user.id, d.id, d.mime),
      datum_privole: vrsta === 'gdpr_privola' ? datumPrivole || null : null,
      unio: user.id,
    };
    let prethodni = await supabase
      .from('dokumenti')
      .select('id, sha256, stanje, kucanstvo_id, vrsta')
      .eq('id', d.id)
      .maybeSingle();
    if (prethodni.error) throw new Error(porukaGreske(prethodni.error));
    if (!prethodni.data) {
      // Nakon osvježavanja kartice prepoznaj istu datoteku istog autora i veze.
      let isti = supabase
        .from('dokumenti')
        .select('id, sha256, stanje, kucanstvo_id, vrsta')
        .eq('unio', user.id)
        .eq('sha256', d.sha256)
        .eq('mime', d.mime)
        .eq('vrsta', vrsta)
        .eq('arhivirano', false);
      isti = kucanstvoId
        ? isti.eq('kucanstvo_id', kucanstvoId)
        : isti.is('kucanstvo_id', null);
      prethodni = await isti
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (prethodni.error) throw new Error(porukaGreske(prethodni.error));
    }
    if (prethodni.data) {
      if (
        prethodni.data.sha256 !== d.sha256 ||
        prethodni.data.kucanstvo_id !== kucanstvoId ||
        prethodni.data.vrsta !== vrsta
      )
        throw new Error(
          'Pripremljeni dokument pripada drugom unosu. Ponovno ga odaberi.',
        );
      if (prethodni.data.stanje === 'spremno') {
        ponovljeno++;
        continue;
      }
      podaci.id = prethodni.data.id;
      podaci.putanja = putanjaDokumenta(user.id, podaci.id, d.mime);
    } else {
      const { error } = await supabase.from('dokumenti').insert(podaci);
      if (error) throw new Error(porukaGreske(error));
    }
    const { error: greskaSlanja } = await supabase.storage
      .from(SPREMNIK_DOKUMENATA)
      .upload(podaci.putanja, d.datoteka, {
        contentType: d.mime,
        upsert: false,
        cacheControl: '0',
      });
    if (
      greskaSlanja &&
      String(greskaSlanja.statusCode) !== '409' &&
      !/already exists|duplicate/i.test(greskaSlanja.message)
    )
      throw new Error(
        'Slanje datoteke nije uspjelo. Provjeri vezu i pokušaj ponovno.',
      );
    const { error } = await supabase.rpc('potvrdi_dokument', {
      p_id: podaci.id,
    });
    if (error) throw new Error(porukaGreske(error));
    poslano++;
  }
  return { poslano, ponovljeno };
}

export async function poveznicaDokumenta(dokument) {
  const { data, error } = await supabase.storage
    .from(SPREMNIK_DOKUMENATA)
    .createSignedUrl(dokument.putanja, 60, {
      download: dokument.izvorni_naziv,
    });
  if (error)
    throw new Error(
      'Dokument nije dostupan za preuzimanje. Osvježi popis i pokušaj ponovno.',
    );
  return data.signedUrl;
}
export async function arhivirajDokument(id, arhivirano) {
  const { error } = await supabase
    .from('dokumenti')
    .update({ arhivirano })
    .eq('id', id)
    .select('id')
    .single();
  if (error) throw new Error(porukaGreske(error));
}

/** Dohvat je vezan uz kućanstvo i njegovu verziju; stariji odgovor ne prepisuje noviji odabir. */
export function usePovijestKucanstva(kucanstvoId, verzija) {
  const [stanje, postaviStanje] = useState({ kljuc: '', zapisi: [], ucitava: false, greska: null, imaJos: false });
  const zahtjev = useRef(0);
  const kljuc = `${kucanstvoId ?? ''}:${verzija ?? ''}`;
  const dohvati = useCallback(async (prijeId = null) => {
    const redni = ++zahtjev.current;
    if (!kucanstvoId) return;
    postaviStanje((s) => ({ ...s, kljuc, zapisi: s.kljuc === kljuc ? s.zapisi : [], ucitava: true, greska: null }));
    try {
      let upit = supabase.from('povijest_kucanstva')
        .select('id,entitet,zapis_id,korisnik_id,korisnik_naziv,vrijeme,transakcija,prije,poslije,radnja')
        .eq('kucanstvo_id', kucanstvoId).order('id', { ascending: false }).limit(50);
      if (prijeId != null) upit = upit.lt('id', prijeId);
      const { data, error } = await upit;
      if (error) throw error;
      if (redni === zahtjev.current) postaviStanje((s) => ({
        kljuc, zapisi: prijeId != null && s.kljuc === kljuc ? [...s.zapisi, ...(data ?? [])] : data ?? [],
        ucitava: false, greska: null, imaJos: data?.length === 50,
      }));
    } catch (e) {
      if (redni === zahtjev.current) postaviStanje((s) => ({ ...s, ucitava: false, greska: porukaGreske(e) }));
    }
  }, [kucanstvoId, kljuc]);
  useEffect(() => {
    dohvati();
    return () => { zahtjev.current++; };
  }, [dohvati]);
  const vidljivo = stanje.kljuc === kljuc ? stanje : { zapisi: [], ucitava: !!kucanstvoId, greska: null, imaJos: false };
  return {
    ...vidljivo,
    osvjezi: () => dohvati(),
    ucitajJos: () => !vidljivo.ucitava && dohvati(vidljivo.zapisi.at(-1)?.id),
  };
}
